import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { addNotification } from 'Components/Notifications/actions';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { GlobalsContext } from 'Shared/context';
import { trackMP } from 'Shared/mp';

import { EMOJI_BUBBLE_ANIMATION_TIME } from '@/components/LiveStream/constants';
import { UserState } from '@/redux/slices/user/types';

import { toggleShowEmojiBasket } from '../../../../../redux/actions';
import style from './style.scss';

export const EmojiBasket = ({ className, tabIndex }: { className: string; tabIndex?: number }) => {
  const dispatch = useDispatch();
  const { socket } = useContext(GlobalsContext);
  const [basketInd, setBasketInd] = useState(0);
  const showEmojiBasket = useSelector((state: any) => state.mainStream.interactionState?.showEmojiBasket);
  const habitatInfo = useSelector((state: any) => state.habitat.habitatInfo);
  const user = useSelector((state: { user: UserState }) => state.user);
  const [disabled, setDisabled] = useState(false);
  const disabledTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const habitatId = habitatInfo?._id;
  const emojis = habitatInfo?.emojiDrops || [];
  const cameraId = habitatInfo?.selectedCamera?._id;

  const { bannedUntil, userId } = user;

  const emojiItems = useMemo(() => emojis[basketInd]?.items.filter((item: any) => !!item) || [], [basketInd, emojis]);
  const availableEmojis = useMemo(() => emojis.reduce((acc: any, { items = [] } = {}) => [...acc, ...items], []), [emojis]);

  const handleDropEmoji = useCallback(
    (emojiSrc: string) => {
      if (bannedUntil && new Date(bannedUntil) > new Date()) {
        addNotification({
          id: `banned-${Math.random()}`,
          type: 'toast',
          text: `You have been timed out and cannot use emoji drops until ${new Date(bannedUntil).toLocaleTimeString()}`,
          timeout: 6000,
        });
        return;
      }

      const decodedUrl = decodeURI(emojiSrc);
      if (availableEmojis?.includes(decodedUrl)) {
        setDisabled(true);
        socket?.emit('userDroppedBubbleEmoji', {
          userId,
          habitatId,
          cameraId,
          path: emojiSrc,
        });
        disabledTimeoutRef.current = setTimeout(() => setDisabled(false), EMOJI_BUBBLE_ANIMATION_TIME);
        trackMP('drop-bubble-emoji', {
          emojiPath: emojiSrc,
          habitatName: habitatInfo?.title,
          habitatId: habitatInfo?._id,
          cameraId,
        });
      }
    },
    [availableEmojis, bannedUntil, cameraId, habitatId, socket, userId],
  );

  useEffect(() => () => disabledTimeoutRef?.current && clearTimeout(disabledTimeoutRef.current), []);

  useEffect(() => {
    if (typeof tabIndex === 'number') {
      setBasketInd(tabIndex);
    }
  }, [tabIndex]);

  if (!showEmojiBasket || !emojis) {
    return null;
  }

  return (
    <div className={clsx(style.emojiContainer, className)}>
      <div className={style.emojiInner}>
        <div className={clsx(style.text, style.title)}>
          <span>Click a sticker to drop!</span>
          {toggleShowEmojiBasket && (
            <button type="button" className={style.close} onClick={() => dispatch(toggleShowEmojiBasket())}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>

        <div className={clsx(style.itemsWrapper, style.customScrollBar)}>
          <div className={style.items}>
            {emojiItems.map((url: string) => (
              <div
                role="button"
                tabIndex={0}
                key={url}
                disabled={disabled}
                className={style.basketItem}
                // Need to setTimeout due to click handler in InteractiveAreaHandler clearing the
                // Emoji Sticker Mode when clicked outside InteractiveAreaHandler
                onClick={() => !disabled && handleDropEmoji(url)}
              >
                <img
                  className={clsx(disabled && style.disabledImage)}
                  disabled={disabled}
                  draggable={false}
                  src={encodeURI(url)}
                  alt=""
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {emojis.length > 1 && (
        <div className={style.emojiBasketTabs}>
          {emojis.map(({ icon }: any, ind: number) => (
            <button
              key={icon}
              type="button"
              className={clsx(style.tabButton, { [style.active]: ind === basketInd })}
              onClick={() => setBasketInd(ind)}
            >
              <img src={icon} alt={`tab ${ind + 1}`} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmojiBasket;
